import React from 'react';
import { Box, Typography, Stack, Divider } from '@mui/material';
import { useBoardColors } from '../../hooks/useBoardColors';

// Standard preset colors for widget backgrounds
const DEFAULT_PRESETS = [
  { color: 'transparent', label: 'שקוף' },
  { color: '#ffffff', label: 'לבן' },
  { color: '#fef9ee' },
  { color: '#f0f9ff' },
  { color: '#fce7f3' },
  { color: '#f3e8ff' },
  { color: '#dcfce7' },
  { color: '#fee2e2' },
];

export const SWATCH_PRESET_VALUES = DEFAULT_PRESETS.map(p => p.color);

/**
 * Reusable color swatch picker with:
 * - Board palette section (colors currently used on the board)
 * - Standard preset colors
 * - Custom color picker button
 *
 * Props:
 *   value       - current color value (hex string, 'transparent', or '')
 *   onChange    - callback(newColor)
 *   size        - swatch size in px (default 28)
 *   showDefaultOption - show an empty-string "ברירת מחדל" option (default false)
 */
const SectionHeader = ({ dotColor, children }) => (
  <Stack direction="row" alignItems="center" spacing={0.5} mb={0.75}>
    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }} />
    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#374151', lineHeight: 1 }}>
      {children}
    </Typography>
  </Stack>
);

export function ColorSwatchPicker({ value, onChange, size = 28, showDefaultOption = false }) {
  const boardColors = useBoardColors();

  // Board palette: valid hex colors not already in presets
  const boardPalette = boardColors.filter(
    c => !SWATCH_PRESET_VALUES.includes(c) && c !== 'transparent'
  );

  // Is the current value a custom color (not in presets, not in board palette)?
  const isCustom = value && value !== 'transparent' && value !== '' &&
    !SWATCH_PRESET_VALUES.includes(value) && !boardPalette.includes(value);

  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      {/* Board palette section */}
      {boardPalette.length > 0 && (
        <>
          <Box sx={{ flex: 1 }}>
            <SectionHeader dotColor="#3b82f6">צבעי הלוח</SectionHeader>
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {boardPalette.map(color => (
                <Box
                  key={color}
                  onClick={() => onChange(color)}
                  title={color}
                  sx={{
                    width: size, height: size, borderRadius: 1, cursor: 'pointer',
                    bgcolor: color,
                    border: value === color ? '2px solid #111827' : '1px solid #e5e7eb',
                    transition: 'all 0.15s',
                    '&:hover': { borderColor: '#9ca3af' },
                  }}
                />
              ))}
            </Stack>
          </Box>
          <Divider orientation="vertical" flexItem sx={{ borderColor: '#e5e7eb' }} />
        </>
      )}

      {/* Presets + custom picker */}
      <Box sx={{ flex: 1 }}>
        {boardPalette.length > 0 && (
          <SectionHeader dotColor="#d1d5db">צבעים בסיסיים</SectionHeader>
        )}
        <Stack direction="row" flexWrap="wrap" gap={0.75}>
        {/* Optional "ברירת מחדל" option (empty-string = use template default) */}
        {showDefaultOption && (
          <Box
            onClick={() => onChange('')}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', cursor: 'pointer' }}
          >
            <Box sx={{
              width: size, height: size, borderRadius: 1,
              background: 'linear-gradient(135deg, #fdf6e3 0 33%, #1e3a5f 33% 66%, #f0eeec 66% 100%)',
              border: value === '' ? '2px solid #111827' : '1px solid #e5e7eb',
              transition: 'all 0.15s', '&:hover': { borderColor: '#9ca3af' }, cursor: 'pointer',
            }} />
            <Typography sx={{ fontSize: '0.6rem', color: value === '' ? '#111827' : '#9ca3af', fontWeight: value === '' ? 600 : 400, lineHeight: 1 }}>
              ברירת מחדל
            </Typography>
          </Box>
        )}

        {/* Preset swatches */}
        {DEFAULT_PRESETS.map(({ color, label }) => (
          <Box
            key={color}
            onClick={() => onChange(color)}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', cursor: 'pointer' }}
          >
            <Box sx={{
              width: size, height: size, borderRadius: 1,
              bgcolor: color === 'transparent' ? undefined : color,
              backgroundImage: color === 'transparent'
                ? 'repeating-conic-gradient(#e5e7eb 0% 25%, white 0% 50%) 0 0 / 10px 10px'
                : undefined,
              border: value === color ? '2px solid #111827' : '1px solid #e5e7eb',
              transition: 'all 0.15s', '&:hover': { borderColor: '#9ca3af' }, cursor: 'pointer',
            }} />
            {label && (
              <Typography sx={{ fontSize: '0.6rem', color: value === color ? '#111827' : '#9ca3af', fontWeight: value === color ? 600 : 400, lineHeight: 1 }}>
                {label}
              </Typography>
            )}
          </Box>
        ))}

        {/* Custom color picker button */}
        <Box
          component="label"
          title="צבע מותאם"
          sx={{
            width: size, height: size, borderRadius: 1, cursor: 'pointer', overflow: 'hidden',
            border: isCustom ? '2px solid #111827' : '1px solid #e5e7eb',
            position: 'relative',
            background: isCustom
              ? `linear-gradient(to bottom, transparent 60%, ${value} 60%), linear-gradient(135deg, #f87171 0%, #facc15 25%, #4ade80 50%, #60a5fa 75%, #a78bfa 100%)`
              : 'linear-gradient(135deg, #f87171 0%, #facc15 25%, #4ade80 50%, #60a5fa 75%, #a78bfa 100%)',
            '&:hover': { borderColor: '#9ca3af' },
          }}
        >
          <Box
            component="input"
            type="color"
            value={!value || value === 'transparent' ? '#ffffff' : value}
            onChange={(e) => onChange(e.target.value)}
            sx={{ position: 'absolute', top: 0, left: 0, width: '200%', height: '200%', opacity: 0, cursor: 'pointer' }}
          />
        </Box>
        </Stack>
      </Box>
    </Stack>
  );
}

